import { v4 as uuidv4 } from 'uuid';
import { MAPPING_TYPE_UI, REQUIREMENT_OPTIONS } from '../const/mapping';

const DEFAULT_TRANSFORMATION_OBJ = {
  simple: null,
  unique: null,
  chain: null,
};

// HELPER RECURSIVE FOR CONDITIONS
const addUuidToCondition = (condition) => {
  if (!condition) return null;

  const newCondition = { ...condition, uuid: uuidv4() };

  if (newCondition.conditions && Array.isArray(newCondition.conditions)) {
    newCondition.conditions = newCondition.conditions.map(addUuidToCondition);
  }

  return newCondition;
};

// UI HELPERS
export const getTransformationTypeLabel = (typeValue) => {
  return MAPPING_TYPE_UI[typeValue]?.label || typeValue;
};

export const detectTransformationType = (fieldMapping, transformationRules) => {
  const targetField = fieldMapping.target_field;
  if (transformationRules) {
    for (const r of transformationRules) {
      if (r.unique_transformation) {
        const field = r.unique_transformation.transformation.field;
        if (field === targetField) return 'unique';
      }
      if (r.transformation_chain) {
        for (const step of r.transformation_chain) {
          const t = step.transformation || step.default_transformation;
          if (t && t.field === targetField) return 'chain';
        }
      }
    }
  }

  if (fieldMapping.source_field !== null && fieldMapping.source_field !== '') {
    return 'simple';
  }

  return 'none';
};

// FORMAT RAW JSON DATAS FOR DYNAMIC HANDLE IN UI
export const formatToUI = (mappingDatas) => {
  if (!mappingDatas || !mappingDatas.field_mappings) return [];

  return mappingDatas.field_mappings.map((field_mapping) => {
    const transformationType = detectTransformationType(
      field_mapping,
      mappingDatas.transformation_rules
    );

    // Build the transformation object based on type
    let transformation = { ...DEFAULT_TRANSFORMATION_OBJ };
    const targetField = field_mapping.target_field;

    if (transformationType === 'unique') {
      const rule = mappingDatas.transformation_rules?.find(
        (r) => r.unique_transformation?.transformation?.field === targetField
      );
      if (rule) {
        transformation.unique = { ...rule.unique_transformation };
        if (transformation.unique.condition) {
          transformation.unique.condition = addUuidToCondition(
            transformation.unique.condition
          );
        }
      }
    } else if (transformationType === 'chain') {
      const rule = mappingDatas.transformation_rules?.find((r) =>
        r.transformation_chain?.some(
          (step) =>
            step.transformation?.field === targetField ||
            step.default_transformation?.field === targetField
        )
      );
      if (rule) {
        const chain = rule.transformation_chain;
        const steps = [];
        let default_transformation = null;

        chain.forEach((step) => {
          if (step.default_transformation) {
            default_transformation = step.default_transformation;
          } else {
            const newStep = { ...step, uuid: uuidv4() };
            if (newStep.condition) {
              newStep.condition = addUuidToCondition(newStep.condition);
            }
            steps.push(newStep);
          }
        });

        transformation.chain = {
          steps,
          default_transformation,
        };
      }
    } else if (transformationType === 'simple') {
      transformation.simple = { source_field: field_mapping.source_field };
    }

    return {
      uuid: uuidv4(),
      target_field: field_mapping.target_field,
      source_field: field_mapping.source_field,
      transformation_type: transformationType,
      lang: field_mapping.destination_language || 'fr',
      transformation: transformation,
    };
  });
};

// Opérateurs d'égalité positive pour lesquels une condition.value = [a, b, ...]
// est éclatée en N conditions successives avec value = a, value = b, ...
// (fan-out safe car chain = first-match-wins).
const EQUALITY_OPERATORS_FOR_ARRAY = ['equals', 'str_equals', 'str_i_equals'];

const expandArrayValueSteps = (steps) => {
  return steps.flatMap((step) => {
    const condition = step.condition;
    if (
      !condition ||
      !Array.isArray(condition.value) ||
      !EQUALITY_OPERATORS_FOR_ARRAY.includes(condition.operator)
    ) {
      return [step];
    }
    return condition.value.map((singleValue) => ({
      ...step,
      condition: { ...condition, value: singleValue },
    }));
  });
};

// HELPER RECURSIVE FOR CLEANING UUIDS FROM CONDITIONS
const removeUuidFromCondition = (condition) => {
  if (!condition) return null;
  // eslint-disable-next-line no-unused-vars
  const { uuid, ...rest } = condition;
  if (rest.conditions && Array.isArray(rest.conditions)) {
    rest.conditions = rest.conditions.map(removeUuidFromCondition);
  }
  return rest;
};

// HELPER RECURSIVE FOR CLEANING UUIDS FROM STEPS
const removeUuidFromStep = (step) => {
  if (!step) return null;
  // eslint-disable-next-line no-unused-vars
  const { uuid, ...rest } = step;

  if (rest.condition) {
    rest.condition = removeUuidFromCondition(rest.condition);
  }
  return rest;
};

// ============================================================================
// UPDATE MAPPING DETAILS WITH MODIFIED FIELD
// ============================================================================

/**
 * Updates the mappingDetails object with the modified field data.
 * This function handles:
 * - Updating or adding field_mappings entry
 * - Removing old transformation_rules for the target_field
 * - Adding new transformation_rules based on transformation_type
 *
 * @param {Object} mappingDetails - The current mapping details from the API
 * @param {Object} modifiedField - The modified field from the UI (selectedField)
 * @returns {Object} - The updated mappingDetails object ready for API
 */
export const updateMappingWithField = (mappingDetails, modifiedField) => {
  if (!mappingDetails || !modifiedField) {
    return mappingDetails;
  }

  const transformationType = modifiedField.transformation_type;

  // Initialize transformations if not present
  const currentTransformations = mappingDetails.transformations || {};
  const currentFieldMappings = currentTransformations.field_mappings || [];
  const currentTransformationRules =
    currentTransformations.transformation_rules || [];

  // 1. UPDATE FIELD_MAPPINGS
  const updatedFieldMappings = updateFieldMappings(
    currentFieldMappings,
    modifiedField
  );

  // 2. UPDATE TRANSFORMATION_RULES
  const updatedTransformationRules = updateTransformationRules(
    currentTransformationRules,
    modifiedField
  );

  // 3. BUILD AND RETURN UPDATED MAPPING DETAILS
  return {
    mp_category_id: mappingDetails.mp_category.id,
    store_id: mappingDetails.store.id,
    name:
      mappingDetails.name ||
      `${mappingDetails.mp_category.name} - ${mappingDetails.store.name}`,
    transformations: {
      field_mappings: updatedFieldMappings,
      transformation_rules:
        transformationType === 'simple' || transformationType === 'none'
          ? updatedTransformationRules.length > 0
            ? updatedTransformationRules
            : undefined
          : updatedTransformationRules,
    },
  };
};

/**
 * Updates the field_mappings array with the modified field data.
 * If an entry for target_field exists, it's updated. Otherwise, a new entry is added.
 *
 * @param {Array} fieldMappings - Current field_mappings array
 * @param {Object} modifiedField - The modified field from UI
 * @returns {Array} - Updated field_mappings array
 */
const updateFieldMappings = (fieldMappings, modifiedField) => {
  const targetField = modifiedField.target_field;
  const transformationType = modifiedField.transformation_type;

  // Determine source_field based on transformation_type
  let sourceField = null;
  if (transformationType === 'simple') {
    sourceField = modifiedField.transformation?.simple?.source_field || null;
  } else if (transformationType === 'unique') {
    // Extract source fields from unique transformation
    sourceField = extractSourceFieldsFromTransformation(
      modifiedField.transformation?.unique?.transformation
    );
  } else if (transformationType === 'chain') {
    // Extract source fields from all chain steps
    sourceField = extractSourceFieldsFromChain(
      modifiedField.transformation?.chain
    );
  }

  // Create the new field mapping entry
  const newFieldMapping = {
    source_field: sourceField,
    target_field: targetField,
  };

  // Add destination_language if present
  if (modifiedField.lang) {
    newFieldMapping.destination_language = modifiedField.lang;
  }

  // Check if entry already exists for this target_field
  const existingIndex = fieldMappings.findIndex(
    (fm) => fm.target_field === targetField
  );

  if (existingIndex !== -1) {
    // Update existing entry
    const updatedMappings = [...fieldMappings];
    updatedMappings[existingIndex] = newFieldMapping;
    return updatedMappings;
  } else {
    // Add new entry
    return [...fieldMappings, newFieldMapping];
  }
};

/**
 * Extracts source fields from a single transformation object.
 * Handles various transformation types including:
 * - get_attribute: extracts from params.attr
 * - concatenate, set_first_attribute_value: extracts from params.fields
 * - add_prefix, add_suffix, add_prefix_suffix, multiply, divide,
 *   uppercase, lowercase, translate: extracts from source_field
 *
 * @param {Object} transformation - The transformation object
 * @returns {Array} - Array of source field names
 */
const extractSourceFieldsFromSingleTransformation = (transformation) => {
  if (!transformation || !transformation.type) {
    return [];
  }

  const fields = [];
  const params = transformation.params || {};

  switch (transformation.type) {
    case 'get_attribute':
      // Extract the attribute name from params.attr
      if (params.attr) {
        fields.push(params.attr);
      }
      break;

    case 'concatenate':
    case 'set_first_attribute_value':
      // Extract all attribute values from params.fields
      if (params.fields && Array.isArray(params.fields)) {
        params.fields.forEach((field) => {
          if (field.type === 'attribute' && field.value) {
            fields.push(field.value);
          }
        });
      }
      break;

    case 'add_prefix':
    case 'add_suffix':
    case 'add_prefix_suffix':
    case 'multiply':
    case 'divide':
    case 'uppercase':
    case 'lowercase':
    case 'translate':
    case 'truncate':
      // These transformations have a source_field property
      if (transformation.source_field) {
        fields.push(transformation.source_field);
      }
      break;

    case 'list':
      // Extract all elements from params.elements
      if (params.elements && Array.isArray(params.elements)) {
        params.elements.forEach((element) => {
          if (element.type === 'attribute' && element.value) {
            fields.push(element.value);
          }
        });
      }
      break;

    default:
      // Other transformation types don't have source fields to extract
      break;
  }

  return fields;
};

/**
 * Extracts source fields from a transformation and returns them as a comma-separated string.
 *
 * @param {Object} transformation - The transformation object
 * @returns {string|null} - Comma-separated source fields or null if none
 */
const extractSourceFieldsFromTransformation = (transformation) => {
  const fields = extractSourceFieldsFromSingleTransformation(transformation);

  if (fields.length === 0) {
    return null;
  }

  // Remove duplicates and join with comma
  const uniqueFields = [...new Set(fields)];
  return uniqueFields.join(', ');
};

/**
 * Extracts source fields from all steps in a transformation chain.
 * Aggregates fields from all steps and the default transformation.
 *
 * @param {Object} chainData - The chain transformation data
 * @returns {string|null} - Comma-separated source fields or null if none
 */
const extractSourceFieldsFromChain = (chainData) => {
  if (!chainData) {
    return null;
  }

  const allFields = [];

  // Extract from steps
  if (chainData.steps && Array.isArray(chainData.steps)) {
    chainData.steps.forEach((step) => {
      const stepFields = extractSourceFieldsFromSingleTransformation(
        step.transformation
      );
      allFields.push(...stepFields);
    });
  }

  // Extract from default transformation
  if (chainData.default_transformation) {
    const defaultFields = extractSourceFieldsFromSingleTransformation(
      chainData.default_transformation
    );
    allFields.push(...defaultFields);
  }

  if (allFields.length === 0) {
    return null;
  }

  // Remove duplicates and join with comma
  const uniqueFields = [...new Set(allFields)];
  return uniqueFields.join(', ');
};

/**
 * Updates the transformation_rules array.
 * Removes any existing rules for the target_field and adds new ones if needed.
 *
 * @param {Array} transformationRules - Current transformation_rules array
 * @param {Object} modifiedField - The modified field from UI
 * @returns {Array} - Updated transformation_rules array
 */
const updateTransformationRules = (transformationRules, modifiedField) => {
  const targetField = modifiedField.target_field;
  const transformationType = modifiedField.transformation_type;

  // 1. Remove existing rules for this target_field
  const filteredRules = removeRulesForTargetField(
    transformationRules,
    targetField
  );

  // 2. Add new rule based on transformation_type
  if (transformationType === 'unique' && modifiedField.transformation?.unique) {
    const uniqueRule = buildUniqueTransformationRule(
      modifiedField.transformation.unique,
      targetField
    );
    if (uniqueRule) {
      return [...filteredRules, uniqueRule];
    }
  }

  if (transformationType === 'chain' && modifiedField.transformation?.chain) {
    const chainRule = buildChainTransformationRule(
      modifiedField.transformation.chain,
      targetField
    );
    if (chainRule) {
      return [...filteredRules, chainRule];
    }
  }

  return filteredRules;
};

/**
 * Removes all transformation rules associated with a target_field.
 * Checks both unique_transformation and transformation_chain entries.
 *
 * @param {Array} transformationRules - Current transformation_rules array
 * @param {string} targetField - The target_field to remove rules for
 * @returns {Array} - Filtered transformation_rules array
 */
const removeRulesForTargetField = (transformationRules, targetField) => {
  return transformationRules.filter((rule) => {
    // Check unique_transformation
    if (rule.unique_transformation) {
      const field = rule.unique_transformation.transformation?.field;
      if (field === targetField) {
        return false; // Remove this rule
      }
    }

    // Check transformation_chain
    if (rule.transformation_chain) {
      // Check if any step in the chain targets this field
      const hasTargetField = rule.transformation_chain.some((step) => {
        const transformation =
          step.transformation || step.default_transformation;
        return transformation?.field === targetField;
      });
      if (hasTargetField) {
        return false; // Remove this rule
      }
    }

    return true; // Keep this rule
  });
};

/**
 * Builds a unique_transformation rule from UI data.
 *
 * @param {Object} uniqueData - The unique transformation data from UI
 * @param {string} targetField - The target field name
 * @returns {Object|null} - The transformation rule object or null
 */
const buildUniqueTransformationRule = (uniqueData, targetField) => {
  if (!uniqueData || !uniqueData.transformation) {
    return null;
  }

  return {
    unique_transformation: {
      condition: removeUuidFromCondition(uniqueData.condition),
      transformation: {
        ...uniqueData.transformation,
        field: targetField,
      },
    },
  };
};

/**
 * Builds a transformation_chain rule from UI data.
 *
 * @param {Object} chainData - The chain transformation data from UI
 * @param {string} targetField - The target field name
 * @returns {Object|null} - The transformation rule object or null
 */
const buildChainTransformationRule = (chainData, targetField) => {
  if (!chainData) {
    return null;
  }

  let chainSteps = [];

  // Process steps
  if (chainData.steps && Array.isArray(chainData.steps)) {
    chainData.steps.forEach((step) => {
      const cleanedStep = removeUuidFromStep(step);
      if (cleanedStep) {
        // Ensure the transformation has the target field
        if (cleanedStep.transformation) {
          cleanedStep.transformation = {
            ...cleanedStep.transformation,
            field: targetField,
          };
        }
        chainSteps.push(cleanedStep);
      }
    });
    chainSteps = expandArrayValueSteps(chainSteps);
  }

  // Process default_transformation
  if (chainData.default_transformation) {
    chainSteps.push({
      default_transformation: {
        ...chainData.default_transformation,
        field: targetField,
      },
    });
  }

  if (chainSteps.length === 0) {
    return null;
  }

  return {
    transformation_chain: chainSteps,
  };
};

export const getRequirementInfos = (requirementName) => {
  if (!requirementName) {
    return REQUIREMENT_OPTIONS.find((option) => option.name === 'not_provided');
  }
  return REQUIREMENT_OPTIONS.find((option) => option.name === requirementName);
};
